/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Formik } from 'formik';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import get from 'lodash/get';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import padStart from 'lodash/padStart';
import toLower from 'lodash/toLower';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SmartyAddressField from 'components/SmartyAddressField';
import CancelButton from 'components/buttons/Cancel';
import EditButton from 'components/buttons/Edit';
import SaveButton from 'components/buttons/Save';
import CardActions from 'components/Card/Actions';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';
import { startCase } from 'lib/addressUtils';
import { getTimezone, getTimezoneOptions } from 'lib/timezoneUtils';
// import tracker from 'lib/tracker';
import 'lib/yupValidations';
import {
  UPDATE_USER_PROFILE,
  EMAIL_CHANGE_OTP,
  EMAIL_CHANGE,
} from '../../../schema/apolloMutations/profileMutations';
import { GET_PROFILE_DETAIL } from '../../../schema/apolloQueries/profileQueries';
import { useAudienceTrackerContext } from '../../../lib/trackers/Audience';
import EmailChange from './EmailChange';
import VerifyAlert from './VerifyAlert';

const useStyles = makeStyles((theme) => ({
  address: {
    color: theme.palette.text.primary,
  },
  error: {
    textAlign: 'center',
  },
  list: {
    padding: 0,
    color: theme.palette.text.primary,
  },
  listItem: {
    paddingTop: 0,
    paddingBottom: '20px !important',
    color: theme.palette.text.primary,
  },
  multipleLeftField: {
    flexDirection: 'column',
    width: '49%',
    marginRight: '2%',
  },
  multipleRightField: {
    flexDirection: 'column',
    width: '49%',
  },
  notification: {
    color: '#F99106',
    textAlign: 'center',
  },
}));

const timezoneOptions = getTimezoneOptions();
const validationSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .lowercase()
    .email('Email must be a valid address')
    .required('Email is required'),
  phoneNumber: Yup.string()
    .trim()
    .required('Phone number is required')
    .testPhone(),
  address: Yup.object().shape({
    street1: Yup.string().trim().required('Street address is required'),
    street2: Yup.string().trim(),
    city: Yup.string().trim().required('City is required'),
    stateOrProvince: Yup.string().required('State / Province is required'),
    postalCode: Yup.string()
      .trim()
      .min(5, 'Postal Code must be at least 5 characters')
      .required('Postal code is required'),
    countryId: Yup.string().required('Country is required'),
    timezone: Yup.string().trim(),
    isValid: Yup.boolean().oneOf([true], 'Address invalid.'),
  }),
});

const ProfileAboutMeContactForm = ({ viewer, personId }) => {
  const styles = useStyles();
  const countryOptions = { 226: 'United States' };
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [notificationMessage] = useState(null);
  const { trackProfile } = useAudienceTrackerContext();
  const [isEmailChange, setIsEmailChange] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [isVerifyAlert, setIsVerifyAlert] = useState(false);
  const [timer, setTimer] = useState();
  const [isActive, setIsActive] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');

  // useEffect(() => {
  //   let intervals;
  //   if (isActive && timer > 0) {
  //     intervals = setInterval(() => {
  //       setTimer(timer - 1);
  //     }, 1000);
  //   } else if (timer === 0) {
  //     setIsActive(false);
  //   }

  //   return () => clearInterval(intervals);
  // }, [isActive, timer]);

  useEffect(() => {
    setEmail(get(viewer, 'personInfo.email'));
  }, [viewer]);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setError(null);
  }, []);

  const handleEdit = useCallback(() => {
    setEditing(true);
  }, []);
  const initialValues = useMemo(() => {
    return {
      email: get(viewer, 'personInfo.email') || '',
      phoneNumber: get(viewer, 'phoneNumber') || '',
      address: {
        street1: startCase(toLower(get(viewer, 'addressInfo.street1') || '')),
        street2: startCase(toLower(get(viewer, 'addressInfo.street2') || '')),
        city: startCase(toLower(get(viewer, 'addressInfo.city') || '')),
        stateOrProvince: get(viewer, 'addressInfo.stateOrProvince') || '',
        postalCode: get(viewer, 'addressInfo.postalCode')
          ? padStart(String(get(viewer, 'addressInfo.postalCode')), 5, '0')
          : '', // ensure 0s aren't stripped
        postalAdditional: get(viewer, 'addressInfo.postalAdditional')
          ? padStart(
            String(get(viewer, 'addressInfo.postalAdditional')),
            4,
            '0',
          )
          : '', // ensure 0s aren't stripped
        countryId: get(viewer, 'addressInfo.countryId') || '',
        timezone: get(viewer, 'addressInfo.timezone') || getTimezone(),
        isValid: get(viewer, 'addressInfo.isValid') || false,
        addressLat: get(viewer, 'addressInfo.addressLat') || '',
        addressLong: get(viewer, 'addressInfo.addressLong') || '',
        cityLat: get(viewer, 'addressInfo.cityLat') || '',
        cityLong: get(viewer, 'addressInfo.cityLong') || '',
      },
    };
  }, [viewer]);

  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    refetchQueries: [GET_PROFILE_DETAIL],
  });
  const [emailChangeOtp] = useMutation(EMAIL_CHANGE_OTP);
  const [emailChange] = useMutation(EMAIL_CHANGE, {
    refetchQueries: [GET_PROFILE_DETAIL],
  });
  const getIsGeoCoded = (lat, long) => {
    let isGeocoded = false;
    if (lat && long) {
      isGeocoded = true;
    }
    return isGeocoded;
  };
  const onSendotp = () => {
    emailChangeOtp({
      variables: {
        otp: 0,
        email: newEmail,
      },
    })
      .then((res) => {
        const data = JSON.parse(res.data.emailChangeOtp);
        setIsActive(true);
        setTimer(data.interval * 60);
        setIsEmailChange(true);
      })
      .catch(() => {
        setIsVerifyAlert(true);
        setAlertMsg('This Email is already exists');
      });
  };
  const onEmailChange = () => {
    emailChange({
      variables: {
        otp: Number(otp),
        email: newEmail,
      },
    })
      .then(() => {
        setIsEmailChange(false);
        setEditing(false);
        setOtp('');
        setErrorText('');
      })
      .catch(() => {
        setErrorText('Please enter a valid OTP');
      });
  };

  return (
    <>
      <EmailChange
        isEmailChange={isEmailChange}
        setIsEmailChange={setIsEmailChange}
        onSendotp={onSendotp}
        onEmailChange={onEmailChange}
        newEmail={newEmail}
        isActive={isActive}
        timer={timer}
        setIsActive={setIsActive}
        setTimer={setTimer}
        setErrorText={setErrorText}
        errorText={errorText}
        setOtp={setOtp}
        otp={otp}
      />
      <VerifyAlert setIsVerifyAlert={setIsVerifyAlert} isVerifyAlert={isVerifyAlert} alertMsg={alertMsg} />
      <Formik
        enableReinitialize
        onSubmit={(data) => {
          if (data.address.isValid) {
            const {
              street1,
              city,
              stateOrProvince,
              postalCode,
              postalAdditional,
              isValid,
              addressLat,
              addressLong,
            } = data.address;
            updateUserProfile({
              variables: {
                personId,
                person: {
                  phoneNumber: data.phoneNumber,
                },
                address: {
                  street1,
                  city,
                  stateOrProvince,
                  postalCode,
                  postalAdditional: postalAdditional || '',
                  cityLat: addressLat,
                  cityLong: addressLong,
                  addressLat,
                  addressLong,
                  isValid,
                  isGeocoded: getIsGeoCoded(addressLat, addressLong),
                  country: 'US',
                  countryName: 'UNITED STATES',
                  isBlacklisted: false,
                },
              },
            })
              .then((res) => {
                if (res.data.updateUserProfile === 200) {
                  // eslint-disable-next-line no-shadow
                  const { email, first, last, birthdate, gender } = viewer?.personInfo;
                  const { street2 } = viewer?.addressInfo;
                  trackProfile('profile', {
                    first_name: first,
                    last_name: last,
                    gender,
                    birth_date: moment(birthdate).format(
                      'YYYY-MM-DD[T]HH:mm:ss',
                    ),
                    email,
                    zipCode: postalCode,
                    city,
                    state: stateOrProvince,
                    address: street1 || street2,
                    // kids: kids || childrens.length,
                  });
                  setError(null);
                  setEditing(false);
                }
              })
              .catch(() => {
                // console.log("error", e)
              });
          }
        }}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={validationSchema.isValidSync(initialValues)}
      >
        {useCallback(
          ({
            dirty,
            errors,
            handleBlur,
            isSubmitting,
            isValid,
            resetForm,
            setFieldValue,
            submitForm,
            values,
          }) => {
            const canSave = dirty && isValid && !isSubmitting;
            return editing ? (
              <LoadBlur loading={isSubmitting}>
                {error && <div className={styles.error}>{error}</div>}
                <List className={styles.list}>
                  <ListItem className={styles.listItem}>
                    <TextField
                      disabled={isSubmitting}
                      errorText={errors?.email}
                      label='Email'
                      fullWidth
                      variant='standard'
                      name='email'
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setNewEmail(e.target.value);
                        setFieldValue('email', e.target.value);
                      }}
                      value={values.email}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            {values.email !== email ? (
                              <span
                                onClick={onSendotp}
                                style={{ color: 'orange', cursor: 'pointer' }}
                              >
                                Verify
                              </span>
                            ) : (
                              <></>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListItem>
                  <ListItem className={styles.listItem}>
                    <TextField
                      disabled={isSubmitting}
                      errorText={errors?.phoneNumber || errors?.phone_number}
                      label='Phone Number'
                      fullWidth
                      variant='standard'
                      name='phoneNumber'
                      value={values.phoneNumber}
                      onBlur={handleBlur}
                      onChange={(e) => setFieldValue(
                        'phoneNumber',
                        new AsYouType('US').input(e.target.value),
                      )}
                    />
                  </ListItem>
                  <div style={{ padding: '0 20px', marginTop: '20px' }}>
                    <SmartyAddressField
                      onChange={(data) => {
                        setFieldValue('address', data);
                      }}
                      countries={countryOptions}
                      values={values?.address}
                    />
                  </div>
                </List>
                <CardActions>
                  <CancelButton
                    disabled={isSubmitting}
                    forceLabel
                    onClick={() => {
                      resetForm();
                      handleCancel();
                    }}
                    secondary
                  />
                  <RowSpacer />
                  <SaveButton
                    disabled={!canSave}
                    forceLabel
                    primary={canSave}
                    onClick={() => {
                      if (email === values.email) {
                        submitForm();
                      } else {
                        setAlertMsg('Please Verify New Email');
                        setIsVerifyAlert(true);
                      }
                    }}
                  />
                </CardActions>
              </LoadBlur>
            ) : (
              <Fragment>
                <List className={styles.list}>
                  {notificationMessage && (
                    <div className={styles.notification}>
                      {notificationMessage}
                    </div>
                  )}
                  <ListItem className={styles.listItem}>
                    <ListItemText primary='Email' secondary={values.email} />
                  </ListItem>
                  {values.phoneNumber && (
                    <ListItem className={styles.listItem}>
                      <ListItemText
                        primary='Phone Number'
                        secondary={
                          parsePhoneNumberFromString(
                            values.phoneNumber,
                            'US',
                          )
                          && parsePhoneNumberFromString(
                            values.phoneNumber,
                            'US',
                          ).formatInternational()
                        }
                      />
                    </ListItem>
                  )}
                  {(get(values, 'address.street1')
                    || get(values, 'address.city')) && (
                      <Fragment>
                        <ListItem className={styles.listItem}>
                          <ListItemText
                            primary='Address'
                            secondaryTextLines={1}
                            secondary={(
                              <Fragment>
                                <div>
                                  {values.address.street1}
                                  {values.address.street2
                                    && `, ${values.address.street2}`}
                                </div>
                                <div>
                                  {values.address.city},{' '}
                                  {values.address.stateOrProvince}{' '}
                                  {`${String(values.address.postalCode)}${values.address.postalAdditional
                                    ? `-${String(
                                      values.address.postalAdditional,
                                    )}`
                                    : ''
                                    }`}
                                </div>
                                {values.address.country && (
                                  <div>{values.address.country.displayName}</div>
                                )}
                              </Fragment>
                            )}
                          />
                        </ListItem>
                        {values.address.timezone && (
                          <ListItem className={styles.listItem}>
                            <ListItemText
                              primary='Timezone'
                              secondary={timezoneOptions[values.address.timezone]}
                            />
                          </ListItem>
                        )}
                      </Fragment>
                    )}
                </List>
                <CardActions>
                  <RowSpacer />
                  <EditButton forceLabel onClick={handleEdit} />
                </CardActions>
              </Fragment>
            );
          },
          [
            countryOptions,
            editing,
            error,
            handleCancel,
            handleEdit,
            notificationMessage,
            styles,
          ],
        )}
      </Formik>
    </>
  );
};

ProfileAboutMeContactForm.propTypes = {
  viewer: PropTypes.object.isRequired,
  personId: PropTypes.object.isRequired,
};

export default ProfileAboutMeContactForm;
